
import { DoctorScoreMetrics, ClinicScoreMetrics, DoctorScore, ClinicScore } from '@/types/scoring';

// Weights for doctor score calculation
const DOCTOR_SCORE_WEIGHTS = {
  responseTimeToPatients: 0.2,
  planAcceptanceRate: 0.2,
  consultationAttendance: 0.15,
  treatmentReportsUploaded: 0.15,
  paymentCompliance: 0.15,
  adEngagement: 0.15,
};

// Weights for clinic score calculation
const CLINIC_SCORE_WEIGHTS = {
  reportSubmissions: 0.25,
  paymentTimeliness: 0.25,
  adEngagement: 0.2,
  doctorActivationRate: 0.15,
  responsivenessToAdmin: 0.15,
};

// Calculate doctor scores
export const calculateDoctorScore = (
  doctorId: string,
  doctorName: string,
  metrics: DoctorScoreMetrics,
  previousScores?: { date: string; score: number }[]
): DoctorScore => {
  const breakdown = [];
  let totalScore = 0;
  let weightSum = 0;

  // Response Time to Patients
  const responseWeight = DOCTOR_SCORE_WEIGHTS.responseTimeToPatients;
  const responseScore = metrics.responseTimeToPatients;
  breakdown.push({
    category: 'Response Time',
    score: responseScore,
    weight: responseWeight * 100,
    weightedScore: responseScore * responseWeight,
    description: 'Based on how quickly doctor replies to new messages',
  });
  totalScore += responseScore * responseWeight;
  weightSum += responseWeight;

  // Plan Acceptance Rate
  const planWeight = DOCTOR_SCORE_WEIGHTS.planAcceptanceRate;
  const planScore = metrics.planAcceptanceRate;
  breakdown.push({
    category: 'Plan Acceptance',
    score: planScore,
    weight: planWeight * 100,
    weightedScore: planScore * planWeight,
    description: '% of sent plans that are accepted by patients',
  });
  totalScore += planScore * planWeight;
  weightSum += planWeight;

  // Consultation Attendance
  const attendanceWeight = DOCTOR_SCORE_WEIGHTS.consultationAttendance;
  const attendanceScore = metrics.consultationAttendance;
  breakdown.push({
    category: 'Consultation Attendance',
    score: attendanceScore,
    weight: attendanceWeight * 100,
    weightedScore: attendanceScore * attendanceWeight,
    description: 'Ratio of attended vs missed consultations',
  });
  totalScore += attendanceScore * attendanceWeight;
  weightSum += attendanceWeight;

  // Treatment Reports Uploaded
  const reportsWeight = DOCTOR_SCORE_WEIGHTS.treatmentReportsUploaded;
  const reportsScore = metrics.treatmentReportsUploaded;
  breakdown.push({
    category: 'Treatment Reports',
    score: reportsScore,
    weight: reportsWeight * 100,
    weightedScore: reportsScore * reportsWeight,
    description: 'Before/after images and treatment documentation',
  });
  totalScore += reportsScore * reportsWeight;
  weightSum += reportsWeight;

  // Payment Compliance
  const paymentWeight = DOCTOR_SCORE_WEIGHTS.paymentCompliance;
  const paymentScore = metrics.paymentCompliance;
  breakdown.push({
    category: 'Payment Compliance',
    score: paymentScore,
    weight: paymentWeight * 100,
    weightedScore: paymentScore * paymentWeight,
    description: 'Timely transfer of due percentages to platform',
  });
  totalScore += paymentScore * paymentWeight;
  weightSum += paymentWeight;

  // Optional: Ad Engagement
  if (metrics.adEngagement !== undefined) {
    const adWeight = DOCTOR_SCORE_WEIGHTS.adEngagement;
    const adScore = metrics.adEngagement;
    breakdown.push({
      category: 'Ad Engagement',
      score: adScore,
      weight: adWeight * 100,
      weightedScore: adScore * adWeight,
      description: 'Based on ad activity and click-through rates',
    });
    totalScore += adScore * adWeight;
    weightSum += adWeight;
  }

  // Normalize score to 100 if not all metrics are present
  if (weightSum > 0 && weightSum < 1) {
    totalScore = totalScore / weightSum;
  }

  // Round to nearest integer
  totalScore = Math.round(totalScore);

  // Calculate trend
  let trend: 'up' | 'down' | 'stable' = 'stable';
  let trendPercentage = 0;

  if (previousScores && previousScores.length > 0) {
    const lastScore = previousScores[previousScores.length - 1].score;
    if (totalScore > lastScore) {
      trend = 'up';
      trendPercentage = ((totalScore - lastScore) / lastScore) * 100;
    } else if (totalScore < lastScore) {
      trend = 'down';
      trendPercentage = ((lastScore - totalScore) / lastScore) * 100;
    }
  }

  // Determine badge
  const badge = totalScore >= 85 ? 'topPerformer' as const : undefined;

  return {
    doctorId,
    doctorName,
    totalScore,
    metrics,
    breakdown,
    previousScores,
    trend,
    trendPercentage,
    lastUpdated: new Date().toISOString(),
    badge,
  };
};

// Calculate clinic scores
export const calculateClinicScore = (
  clinicId: string,
  clinicName: string,
  metrics: ClinicScoreMetrics,
  previousScores?: { date: string; score: number }[]
): ClinicScore => {
  const breakdown = [];
  let totalScore = 0;

  // Report Submissions
  const reportWeight = CLINIC_SCORE_WEIGHTS.reportSubmissions;
  const reportScore = metrics.reportSubmissions;
  breakdown.push({
    category: 'Report Submissions',
    score: reportScore,
    weight: reportWeight * 100,
    weightedScore: reportScore * reportWeight,
    description: '% of doctors submitting proper reports',
  });
  totalScore += reportScore * reportWeight;

  // Payment Timeliness
  const paymentWeight = CLINIC_SCORE_WEIGHTS.paymentTimeliness;
  const paymentScore = metrics.paymentTimeliness;
  breakdown.push({
    category: 'Payment Timeliness',
    score: paymentScore,
    weight: paymentWeight * 100,
    weightedScore: paymentScore * paymentWeight,
    description: 'Clinic compliance in transferring platform share',
  });
  totalScore += paymentScore * paymentWeight;

  // Ad Engagement
  const adWeight = CLINIC_SCORE_WEIGHTS.adEngagement;
  const adScore = metrics.adEngagement;
  breakdown.push({
    category: 'Ad Engagement',
    score: adScore,
    weight: adWeight * 100,
    weightedScore: adScore * adWeight,
    description: 'CTR + number of active/expired campaigns',
  });
  totalScore += adScore * adWeight;

  // Doctor Activation Rate
  const activationWeight = CLINIC_SCORE_WEIGHTS.doctorActivationRate;
  const activationScore = metrics.doctorActivationRate;
  breakdown.push({
    category: 'Doctor Activation',
    score: activationScore,
    weight: activationWeight * 100,
    weightedScore: activationScore * activationWeight,
    description: '% of onboarded doctors who actively use the system',
  });
  totalScore += activationScore * activationWeight;

  // Responsiveness to Admin
  const responsivenessWeight = CLINIC_SCORE_WEIGHTS.responsivenessToAdmin;
  const responsivenessScore = metrics.responsivenessToAdmin;
  breakdown.push({
    category: 'Admin Responsiveness',
    score: responsivenessScore,
    weight: responsivenessWeight * 100,
    weightedScore: responsivenessScore * responsivenessWeight,
    description: 'How quickly clinic reacts to admin notes',
  });
  totalScore += responsivenessScore * responsivenessWeight;

  // Round to nearest integer
  totalScore = Math.round(totalScore);

  // Calculate trend
  let trend: 'up' | 'down' | 'stable' = 'stable';
  let trendPercentage = 0;

  if (previousScores && previousScores.length > 0) {
    const lastScore = previousScores[previousScores.length - 1].score;
    if (totalScore > lastScore) {
      trend = 'up';
      trendPercentage = ((totalScore - lastScore) / lastScore) * 100;
    } else if (totalScore < lastScore) {
      trend = 'down';
      trendPercentage = ((lastScore - totalScore) / lastScore) * 100;
    }
  }

  // Determine tag
  let tag: 'reliablePartner' | 'needsAttention' | undefined;
  if (totalScore >= 85) {
    tag = 'reliablePartner';
  } else if (totalScore < 60) {
    tag = 'needsAttention';
  }

  return {
    clinicId,
    clinicName,
    totalScore,
    metrics,
    breakdown,
    previousScores,
    trend,
    trendPercentage,
    lastUpdated: new Date().toISOString(),
    tag,
  };
};

// Helper to convert minutes to readable format for response time
export const formatResponseTime = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  } else if (minutes < 1440) { // Less than a day
    const hours = Math.floor(minutes / 60);
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  } else {
    const days = Math.floor(minutes / 1440);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
};

// Helper function to get score color based on value
export const getScoreColor = (score: number): string => {
  if (score >= 85) return 'text-green-600 dark:text-green-500';
  if (score >= 70) return 'text-emerald-500 dark:text-emerald-400';
  if (score >= 60) return 'text-yellow-600 dark:text-yellow-500';
  if (score >= 50) return 'text-amber-500 dark:text-amber-400';
  return 'text-red-600 dark:text-red-500';
};

// Helper to get badge variants for different score levels
export const getScoreBadgeVariant = (score: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (score >= 85) return 'default';
  if (score >= 70) return 'secondary';
  if (score >= 50) return 'outline';
  return 'destructive';
};
